import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react";
import { Sentry } from "@/lib/sentry";
import { listingMileageUnits } from "@workspace/countries";
import { normalizeWizardLocationCountry as normalizeLocationCountry, safeProfileListingCountry } from "./listing-wizard-country";

function normalizeWizardLocationCountry(data: WizardData, profileCountryCode?: string | null): WizardData {
  const normalized = normalizeLocationCountry(data, profileCountryCode);
  return { ...normalized, mileage_units: listingMileageUnits(normalized.country_code) };
}

export interface WizardData {
  carId: number | null;
  /**
   * Canonical DB-stored slug for the draft listing. Populated by responses
   * from `prepareForEnrichment` / `updateListing` so the Review & submit
   * preview iframe can request the canonical URL on first load instead of
   * having to follow a `_slugRedirect` round-trip (task #1483).
   *
   * Null when the wizard hasn't created a draft on the server yet, or
   * while the draft still carries the placeholder `prep-{id}` slug.
   */
  slug: string | null;
  make: string;
  /**
   * `car_make_requests.id` for a brand the host has REQUESTED but that isn't
   * yet on the verified list (task #5073). Set when the make picker submits a
   * new-make request, threaded back into the create (`/ai-enrichment/prepare`)
   * and save (PUT) payloads so the server links `cars.pending_make_request_id`
   * deterministically — no reliance on name-matching. Hydrated on resume from
   * the server-confirmed GET signal. Cleared when the host picks a verified
   * brand. `null` when the make is verified or no request has been made.
   */
  pendingMakeRequestId: number | null;
  model: string;
  year: string;
  submodel: string;
  vin: string;
  body_style: string;
  engine: string;
  wheels_and_tires: string;
  brakes: string;
  color: string;
  colour: string;
  mileage: string;
  mileage_is_accurate: boolean;
   /** Derived compatibility metadata: 1 = miles, 2 = km. Never a preference. */
  mileage_units: number | null;
  condition: string;
  transmission: string;
  passengers: string;
  convertible: boolean;
  is_convertible: boolean;
  air_conditioning: boolean;
  has_aircon: boolean;
  left_hand_drive: boolean;
  is_left_hand_drive: boolean;
  all_seatbelts: boolean;
  feature_heat: boolean;
  feature_radio: boolean;
  feature_aux: boolean;
  feature_steering: boolean;
  feature_modified: boolean;
  origin_country: string;
  fuel_type: string;
  classification: string;
  description: string;
  car_history: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country_code: string;

  rental_driving_active: boolean;
  price_driving: string;
  rental_driving_min_duration: string;

  rental_chauffeured_active: boolean;
  chauffeured_hourly_rate: string;
  chauffeured_min_price_hourly_rate: string;

  rental_event_active: boolean;
  price_event: string;
  event_hourly_rate: string;
  event_min_price_hourly_rate: string;
  eventTypeIds: number[];

  deposit: string;
  insured_value: string;
  car_value_range_id: string;
  instant_rental: boolean;
  instant_book_mode: "all" | "selected" | "off";
  instantBookDates: Array<{ start_date: string; end_date: string }>;

  self_drive_delivery_enabled: boolean;
  delivery_enabled: boolean;
  delivery_fee_per_unit: string;
  delivery_free: boolean;
  delivery_maximum_distance: string;
  delivery_radius_free: string;
  delivery_radius_full: string;
  delivery_fee_min: string;
  deliveryAirports: Array<{ airport_code: string; airport_name: string; flat_fee: number; city?: string | null; country?: string | null }>;

  available_from: string;
  available_to: string;
  advance_notice_days: string;
  min_booking_days: string;
  blockedDates: Array<{ start_date: string; end_date: string }>;
  customDailyRates: Array<{ date: string; price: number }>;

  fuel_policy: string;
  mileage_limit: boolean;
  mileage_limit_miles: string;
  mileage_overage_rate: string;
  pets_allowed: boolean;
  no_smoking: boolean;
  allow_decorations: boolean;
  weather_policy: string;
  pickup_instructions: string;
  internal_notes: string;
  valuation_estimate: string;
  valuation_source: string;
  valuation_confidence: string;
  valuation_status: string;
  valuation_suggested_value: string;
  valuation_user_proposed_value: string;
  valuation_choice_required: boolean | null;
  valuation_source_label: string;
  valuation_comps_found: string;
  suggested_daily_rate: string;
  car_story_how_found: string;
  car_story_what_special: string;
  car_story_favorite_memory: string;
  car_story_enhanced: boolean;
  presentation_seen: boolean;
}

const DEFAULT_DATA: WizardData = {
  carId: null,
  slug: null,
  pendingMakeRequestId: null,
  make: "", model: "", year: "", submodel: "", vin: "",
  body_style: "", engine: "", wheels_and_tires: "", brakes: "", color: "", colour: "",
  mileage: "", mileage_is_accurate: true, mileage_units: null,
  condition: "", transmission: "Automatic", passengers: "4",
  convertible: false, is_convertible: false, air_conditioning: false, has_aircon: false, left_hand_drive: true, is_left_hand_drive: true,
  all_seatbelts: false,
  feature_heat: false, feature_radio: false, feature_aux: false, feature_steering: false, feature_modified: false,
  origin_country: "", fuel_type: "", classification: "",
  description: "", car_history: "",
  street: "", city: "", state: "", zip: "", country_code: "",

  rental_driving_active: false, price_driving: "",
  rental_driving_min_duration: "1",

  rental_chauffeured_active: false, chauffeured_hourly_rate: "",
  chauffeured_min_price_hourly_rate: "",

  rental_event_active: false, price_event: "", event_hourly_rate: "",
  event_min_price_hourly_rate: "",
  eventTypeIds: [],

  deposit: "", insured_value: "", car_value_range_id: "", instant_rental: false,
  instant_book_mode: "off",
  instantBookDates: [],

  self_drive_delivery_enabled: false, delivery_enabled: false, delivery_fee_per_unit: "",
  delivery_free: false, delivery_maximum_distance: "",
  delivery_radius_free: "", delivery_radius_full: "", delivery_fee_min: "",
  deliveryAirports: [],

  available_from: "", available_to: "",
  advance_notice_days: "2", min_booking_days: "1",
  blockedDates: [],
  customDailyRates: [],

  fuel_policy: "1", mileage_limit: false,
  mileage_limit_miles: "", mileage_overage_rate: "",
  pets_allowed: false, no_smoking: true, allow_decorations: false, weather_policy: "0",
  pickup_instructions: "", internal_notes: "",
  valuation_estimate: "", valuation_source: "", valuation_confidence: "",
  valuation_status: "", valuation_suggested_value: "", valuation_user_proposed_value: "",
  valuation_choice_required: null,
  valuation_source_label: "", valuation_comps_found: "",
  suggested_daily_rate: "",
  car_story_how_found: "", car_story_what_special: "",
  car_story_favorite_memory: "", car_story_enhanced: false,
  presentation_seen: false,
};

const STORAGE_KEY = "listing_wizard_draft";
const RESET_FLAG_KEY = "listing_wizard_was_reset";
const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

function hydrateWizardData(data: Partial<WizardData>): WizardData {
  const merged = { ...DEFAULT_DATA, ...data };
  return {
    ...merged,
    mileage_units: listingMileageUnits(merged.country_code),
    // Older in-progress drafts used the aggregate delivery flag as the only
    // host choice. Preserve it only when the canonical flag is absent.
    self_drive_delivery_enabled:
      data.self_drive_delivery_enabled ?? data.delivery_enabled ?? false,
  };
}

async function fetchDraft(token: string): Promise<{ id: string; data: WizardData; step: number } | null> {
  try {
    const res = await fetch(`${API_BASE}/listings/drafts`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const { drafts } = await res.json();
    if (drafts && drafts.length > 0) {
      return { id: drafts[0].id, data: drafts[0].data, step: drafts[0].step };
    }
    return null;
  } catch (err) {
    console.error("[useListingWizard] Failed to fetch draft:", err);
    Sentry.captureException(err);
    return null;
  }
}

async function saveDraft(
  token: string,
  draftId: string | null,
  data: WizardData,
  step: number,
): Promise<string | null> {
  try {
    const url = draftId
      ? `${API_BASE}/listings/drafts/${draftId}`
      : `${API_BASE}/listings/drafts`;
    const method = draftId ? "PUT" : "POST";
    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ data, step }),
    });
    if (!res.ok) return draftId;
    const result = await res.json();
    return result.draft?.id || draftId;
  } catch (err) {
    console.error("[useListingWizard] Failed to save draft:", err);
    Sentry.captureException(err);
    return draftId;
  }
}

async function deleteDraft(token: string, draftId: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/listings/drafts/${draftId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    console.error("[useListingWizard] Failed to delete draft:", err);
    Sentry.captureException(err);
  }
}

interface WizardContextValue {
  step: number;
  setStep: (s: number) => void;
  data: WizardData;
  updateData: (partial: Partial<WizardData>) => void;
  resetWizard: () => void;
  isEditing: boolean;
  resumeVisualStep: number | null;
  fieldErrors: Record<string, string>;
  setFieldErrors: (errors: Record<string, string>) => void;
  setFieldError: (field: string, message: string) => void;
  clearFieldError: (field: string) => void;
}

const WizardContext = createContext<WizardContextValue | null>(null);

export function WizardProvider({
  children,
  editData,
  authToken,
  resumeVisualStep,
  profileCountryCode,
}: {
  children: ReactNode;
  editData?: Partial<WizardData> & { carId: number };
  authToken?: string | null;
  resumeVisualStep?: number | null;
  profileCountryCode?: string | null;
}) {
  const isEditing = !!editData?.carId;
  const isResumeMode = isEditing && resumeVisualStep != null;
  const isLoggedIn = !!authToken && !isEditing;

  const [step, setStep] = useState(1);
  const [data, setData] = useState<WizardData>(() => {
    if (editData) {
      return normalizeWizardLocationCountry(
        hydrateWizardData(editData),
        profileCountryCode,
      );
    }
    try {
      const wasReset = sessionStorage.getItem(RESET_FLAG_KEY);
      if (wasReset) {
        sessionStorage.removeItem(RESET_FLAG_KEY);
        localStorage.removeItem(STORAGE_KEY);
        return { ...DEFAULT_DATA };
      }
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.carId) {
          localStorage.removeItem(STORAGE_KEY);
        } else {
          return normalizeWizardLocationCountry(
            hydrateWizardData(parsed),
            profileCountryCode,
          );
        }
      }
    } catch (err) {
      console.error("[useListingWizard] Failed to read draft from localStorage:", err);
      Sentry.captureException(err);
    }
    return {
      ...DEFAULT_DATA,
      country_code: safeProfileListingCountry(profileCountryCode),
    };
  });

  const draftIdRef = useRef<string | null>(null);
  const initializedRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const wasResetRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn || isEditing || initializedRef.current) return;
    initializedRef.current = true;

    try {
      if (sessionStorage.getItem(RESET_FLAG_KEY)) {
        sessionStorage.removeItem(RESET_FLAG_KEY);
        wasResetRef.current = true;
      }
    } catch {}

    if (wasResetRef.current) {
      fetchDraft(authToken!).then((remote) => {
        if (remote?.id) deleteDraft(authToken!, remote.id);
      });
      return;
    }

    fetchDraft(authToken!).then((remote) => {
      if (remote && !remote.data?.carId) {
        draftIdRef.current = remote.id;
        setData(normalizeWizardLocationCountry(
          hydrateWizardData(remote.data),
          profileCountryCode,
        ));
        setStep(remote.step || 1);
      } else if (remote?.id) {
        deleteDraft(authToken!, remote.id);
      }
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (err) {
        console.error("[useListingWizard] Failed to clear localStorage draft:", err);
        Sentry.captureException(err);
      }
    });
  }, [isLoggedIn, isEditing, authToken, profileCountryCode]);

  useEffect(() => {
    const safeCountry = safeProfileListingCountry(profileCountryCode);
    if (!safeCountry) return;
    setData((current) => {
      const normalized = normalizeWizardLocationCountry(current, safeCountry);
      return normalized.country_code === current.country_code ? current : normalized;
    });
  }, [profileCountryCode]);

  useEffect(() => {
    if (isEditing) return;

    const hasUserData = !!(data.make || data.model || data.year);
    if (hasUserData) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (err) {
        console.error("[useListingWizard] Failed to persist draft to localStorage:", err);
        Sentry.captureException(err);
      }
    }

    if (data.presentation_seen && isLoggedIn) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveDraft(authToken!, draftIdRef.current, data, step).then((id) => {
          if (id) draftIdRef.current = id;
        });
      }, 1000);
    }

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [data, step, isEditing, isLoggedIn, authToken]);

  const [fieldErrors, setFieldErrorsState] = useState<Record<string, string>>({});

  const updateData = useCallback((partial: Partial<WizardData>) => {
    setData(prev => {
      const countryChanged = partial.country_code !== undefined
        && partial.country_code.toUpperCase() !== prev.country_code.toUpperCase();
      return {
        ...prev,
        ...partial,
        mileage_units: listingMileageUnits(partial.country_code ?? prev.country_code),
        // A server-side eligibility decision is scoped to the listing's
        // current country. Re-check after a host changes country rather than
        // reusing a stale "not required" result from the previous location.
        ...(countryChanged && partial.valuation_choice_required === undefined
          ? { valuation_choice_required: null }
          : {}),
      };
    });
    // Clear errors for any field the user has just touched.
    setFieldErrorsState(prev => {
      const keys = Object.keys(partial);
      if (keys.length === 0 || Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      for (const k of keys) {
        // Map wizard alias keys -> canonical field name used in the schema.
        const canonical =
          k === "colour" ? "color" :
          k === "country_code" ? "country_code" :
          k;
        if (next[canonical]) { delete next[canonical]; changed = true; }
        if (next[k]) { delete next[k]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, []);

  const setFieldErrors = useCallback((errors: Record<string, string>) => {
    setFieldErrorsState(errors);
  }, []);

  const setFieldError = useCallback((field: string, message: string) => {
    setFieldErrorsState(prev => {
      if (prev[field] === message) return prev;
      return { ...prev, [field]: message };
    });
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrorsState(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const resetWizard = useCallback(() => {
    setData({
      ...DEFAULT_DATA,
      country_code: safeProfileListingCountry(profileCountryCode),
    });
    setStep(1);
    localStorage.removeItem(STORAGE_KEY);
    try {
      sessionStorage.setItem(RESET_FLAG_KEY, "1");
    } catch {}
    if (isLoggedIn && draftIdRef.current) {
      deleteDraft(authToken!, draftIdRef.current);
      draftIdRef.current = null;
    }
    initializedRef.current = false;
    wasResetRef.current = true;
  }, [isLoggedIn, authToken, profileCountryCode]);

  return (
    <WizardContext.Provider value={{ step, setStep, data, updateData, resetWizard, isEditing, resumeVisualStep: resumeVisualStep ?? null, fieldErrors, setFieldErrors, setFieldError, clearFieldError }}>
      {children}
    </WizardContext.Provider>
  );
}

export function useWizard(): WizardContextValue {
  const ctx = useContext(WizardContext);
  if (!ctx) {
    // The most common way this fires in real usage is a Vite HMR
    // race: when this module hot-reloads, the `WizardContext`
    // identity changes; the surrounding `WizardProvider` provides
    // the new context but a consumer briefly bound to the old
    // module reads `null`. Annotating the error helps the boundary
    // upstream (and Sentry) tell apart the dev-only HMR transient
    // from a genuine "consumer is outside any provider" mistake.
    const err = new Error("useWizard must be inside WizardProvider");
    if (import.meta.env?.DEV) {
      console.warn(
        "[useWizard] context was null — likely a Vite HMR transient. " +
        "If this fires repeatedly without an HMR event, a consumer is " +
        "rendering outside <WizardProvider>.",
      );
    }
    throw err;
  }
  return ctx;
}
